import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Divider,
  Stack,
  Link
} from '@mui/material';
import { 
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';

const ContactPage: React.FC = () => {
  const officeLocations = [
    {
      city: 'San Francisco',
      address: '123 Market Street, Suite 400, San Francisco, CA 94105',
      phone: '+1 (415) 555-1234',
      email: 'sf@contractai.com'
    },
    {
      city: 'New York',
      address: '456 Park Avenue, 15th Floor, New York, NY 10022',
      phone: '+1 (212) 555-5678',
      email: 'nyc@contractai.com'
    },
    {
      city: 'London',
      address: '78 Chancery Lane, London, WC2A 1AA, UK',
      phone: '+44 20 5555 9876',
      email: 'london@contractai.com'
    }
  ];

  const contactReasons = [
    'General Inquiry',
    'Sales Question',
    'Technical Support',
    'Partnership Opportunity',
    'Press Inquiry',
    'Career Information',
    'Other'
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ bgcolor: '#1a365d', color: 'white', py: 8, pt: 4 }}>
        <LandingHeader />
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
            Contact Us
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: '700px', mb: 4, opacity: 0.9 }}>
            We'd love to hear from you. Reach out to our team with any questions or inquiries.
          </Typography>
        </Container>
      </Box>
      
      {/* Contact Form Section */}
      <Container maxWidth="lg" sx={{ py: 8, mt: { xs: 0, md: -6 } }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: { xs: 3, md: 5 }, 
                borderRadius: 3,
                boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
              }}
            >
              <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
                Get in Touch
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
                Fill out the form below and our team will get back to you as soon as possible.
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    variant="outlined"
                    required
                    sx={{ mb: 3 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    variant="outlined"
                    required
                    sx={{ mb: 3 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    variant="outlined"
                    type="email"
                    required
                    sx={{ mb: 3 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    variant="outlined"
                    sx={{ mb: 3 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Company"
                    variant="outlined"
                    sx={{ mb: 3 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="reason-label">Reason for Contact</InputLabel>
                    <Select
                      labelId="reason-label"
                      label="Reason for Contact"
                      defaultValue=""
                    >
                      {contactReasons.map((reason, index) => (
                        <MenuItem key={index} value={reason.toLowerCase().replace(/\s+/g, '-')}>
                          {reason}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    variant="outlined"
                    multiline
                    rows={5}
                    required
                    sx={{ mb: 3 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button 
                    variant="contained" 
                    color="primary"
                    size="large"
                    fullWidth
                    sx={{ 
                      py: 1.5, 
                      borderRadius: 2
                    }}
                  >
                    Submit Message
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={5}>
            <Stack spacing={3}>
              <Paper 
                sx={{ 
                  p: 4, 
                  borderRadius: 3,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
                }}
              >
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Contact Information
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                <Stack spacing={3}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <EmailIcon sx={{ color: 'primary.main', mr: 2, mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Email
                      </Typography>
                      <Link href="mailto:info@contractai.com" color="primary" underline="hover">
                        info@contractai.com
                      </Link>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <PhoneIcon sx={{ color: 'primary.main', mr: 2, mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Phone
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        +1 (800) 555-0123
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Monday - Friday, 9am - 6pm EST
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <BusinessIcon sx={{ color: 'primary.main', mr: 2, mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Headquarters
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        123 Market Street, Suite 400
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        San Francisco, CA 94105
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        United States
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Paper>
              
              <Paper 
                sx={{ 
                  p: 4, 
                  borderRadius: 3,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
                }}
              >
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Sales Inquiries
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Interested in learning more about our solutions for your organization?
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <EmailIcon sx={{ color: 'primary.main', mr: 2 }} />
                  <Link href="mailto:sales@contractai.com" color="primary" underline="hover">
                    sales@contractai.com
                  </Link>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <PhoneIcon sx={{ color: 'primary.main', mr: 2 }} />
                  <Typography variant="body2">
                    +1 (800) 555-0199
                  </Typography>
                </Box>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Container>
      
      {/* Office Locations */}
      <Box sx={{ bgcolor: '#f8faff', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom textAlign="center">
            Our Offices
          </Typography>
          <Typography variant="body1" paragraph textAlign="center" sx={{ maxWidth: '700px', mx: 'auto', mb: 6 }}>
            Visit us at one of our global office locations
          </Typography>
          
          <Grid container spacing={4}>
            {officeLocations.map((office, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    borderRadius: 3,
                    transition: '0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      height: 200, 
                      bgcolor: '#1a365d', 
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <Box 
                      sx={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        opacity: 0.2,
                        backgroundImage: `url(https://source.unsplash.com/featured/?${office.city},skyline)`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    />
                    <Typography variant="h4" fontWeight="bold" sx={{ position: 'relative', zIndex: 1 }}>
                      {office.city}
                    </Typography>
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <LocationIcon sx={{ color: 'primary.main', mr: 2, mt: 0.5 }} />
                      <Typography variant="body2" color="text.secondary">
                        {office.address}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PhoneIcon sx={{ color: 'primary.main', mr: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        {office.phone}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <EmailIcon sx={{ color: 'primary.main', mr: 2 }} />
                      <Link href={`mailto:${office.email}`} color="primary" underline="hover">
                        {office.email}
                      </Link>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      
      {/* Map */}
      <Box sx={{ height: '500px', width: '100%', position: 'relative' }}>
        <iframe 
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.0958124233223!2d-122.39973068439202!3d37.79129997975658!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80858062c0f7f595%3A0xd049256548cc0a79!2sMarket%20St%2C%20San%20Francisco%2C%20CA%2C%20USA!5e0!3m2!1sen!2sca!4v1654123456789!5m2!1sen!2sca" 
          width="100%" 
          height="100%" 
          style={{ border: 0 }} 
          allowFullScreen 
          loading="lazy" 
          referrerPolicy="no-referrer-when-downgrade"
          title="Contract AI Headquarters"
        />
      </Box>

      <Footer />
    </Box>
  );
};

export default ContactPage; 